/**
 * Authored level definitions. Data only — no generation logic lives here.
 * The player descends through LEVEL_ORDER one entry at a time.
 */

import type { LevelSpec, RNG } from '../core/types';

const LOBBY: LevelSpec = {
  id: 'level-0',
  name: 'Level 0',
  subtitle: 'The Lobby',
  blurb: 'Roughly six hundred million square miles of randomly segmented empty rooms.',
  gridW: 140,
  gridH: 140,
  cellSize: 3.0,
  ceilingHeight: 3.1,
  palette: {
    wall: 0xd8c97a,
    wallTrim: 0xbfae5e,
    carpet: 0xc0b268,
    carpetStain: 0x8d7f43,
    ceiling: 0xd6cd9a,
    lightTint: 0xfff4c9,
    fog: 0xb9ac67,
    ambient: 0x8a7f4a,
  },
  fogNear: 7,
  fogFar: 54,
  ambientIntensity: 0.34,
  lightSpacing: 15,
  brokenLightChance: 0.18,
  supplyCount: 26,
  supplyTable: [
    { kind: 'almond-water', weight: 26 },
    { kind: 'water-bottle', weight: 16 },
    { kind: 'granola', weight: 20 },
    { kind: 'canned-food', weight: 10 },
    { kind: 'candy', weight: 10 },
    { kind: 'battery', weight: 9 },
    { kind: 'bandage', weight: 5 },
    { kind: 'note', weight: 4 },
  ],
  // The one item the rest of the game is built around. Every level ships
  // batteries, the smiler's only counterplay is the beam, and half the lighting
  // tuning assumes it exists — so it cannot be left to a weighted draw.
  //
  // The note is here for the same reason. Guaranteed items are placed first and
  // from the scored pool, so this one lands on the route through Level 0 rather
  // than in a closet; `survival.ts` deals the rules note into the level's first
  // note spot. A player should have been told what to do about the hound before
  // they meet one.
  guaranteedItems: ['flashlight', 'note'],
  /*
   * Wikidot's Level 0 confirms nothing — "it is unknown whether or not entities
   * exist in Level 0… wanderers have claimed to have seen dark figures stalking
   * them between the corridors." The Bacteria is that line made a body: the
   * Kane Pixels lineage puts its Lifeform on the first level, and a towering
   * black wire-thin humanoid that stands still until it screams is exactly what
   * a dark figure between corridors turns out to be. Smilers and Hounds are the
   * Lobby roster every Backrooms game agrees on.
   *
   * Budget stays at 1. Three kinds is variety across runs, not three at once.
   */
  entityKinds: ['smiler', 'hound', 'bacteria'],
  entityBudget: 1,
  threatScale: 0.7,
  exitCount: 3,
  generator: 'lobby',
};

const WAREHOUSE: LevelSpec = {
  id: 'level-1',
  name: 'Level 1',
  subtitle: 'Habitable Zone',
  blurb: 'Concrete and steel. The hum is quieter here, which is worse.',
  gridW: 150,
  gridH: 150,
  cellSize: 3.2,
  ceilingHeight: 4.6,
  palette: {
    wall: 0x9a968b,
    wallTrim: 0x7c7870,
    carpet: 0x6f6c66,
    carpetStain: 0x4d4a45,
    ceiling: 0x5c5a55,
    lightTint: 0xcfe0ff,
    fog: 0x585a58,
    ambient: 0x494d52,
  },
  fogNear: 9,
  fogFar: 60,
  ambientIntensity: 0.22,
  lightSpacing: 26,
  brokenLightChance: 0.3,
  supplyCount: 22,
  supplyTable: [
    { kind: 'almond-water', weight: 20 },
    { kind: 'water-bottle', weight: 14 },
    { kind: 'granola', weight: 16 },
    { kind: 'canned-food', weight: 14 },
    { kind: 'battery', weight: 13 },
    { kind: 'bandage', weight: 8 },
    { kind: 'pills', weight: 6 },
    { kind: 'note', weight: 5 },
  ],
  /*
   * Wikidot's Level 1 Common Entity List, filtered to what exists here: Hounds
   * ("humanoid dogs"), Smilers, and Clumps ("piles of connected limbs rushing
   * to their preys with high speed"). Wretches and Skin-Stealers are on that
   * list too and were the game's own inventions under those names; the Clump
   * takes the slot and is better documented for this level than either.
   */
  entityKinds: ['hound', 'smiler', 'clump'],
  entityBudget: 2,
  threatScale: 1.0,
  exitCount: 3,
  generator: 'warehouse',
};

/**
 * A one-minute safe interlude. Small enough to hold in your head, which is the
 * whole point of it: it is the only place in the game you can be sure of.
 */
const ALCOVE: LevelSpec = {
  id: 'level-1-2',
  name: 'Level 1.2',
  subtitle: 'The Alcove',
  blurb: 'Someone stacked the chairs. Someone left every light on. They are not here now.',
  gridW: 64,
  gridH: 64,
  cellSize: 3.0,
  ceilingHeight: 3.4,
  palette: {
    wall: 0xcfc59b,
    wallTrim: 0xa89a6c,
    carpet: 0x9c8f6a,
    carpetStain: 0x7a6f50,
    ceiling: 0xdad3b4,
    lightTint: 0xfff1d6,
    fog: 0xa89d78,
    ambient: 0x8f8763,
  },
  fogNear: 13,
  fogFar: 58,
  ambientIntensity: 0.5,
  lightSpacing: 9,
  brokenLightChance: 0.03,
  supplyCount: 7,
  supplyTable: [
    { kind: 'almond-water', weight: 22 },
    { kind: 'water-bottle', weight: 14 },
    { kind: 'canned-food', weight: 16 },
    { kind: 'granola', weight: 14 },
    { kind: 'battery', weight: 14 },
    { kind: 'bandage', weight: 10 },
    { kind: 'pills', weight: 6 },
    { kind: 'note', weight: 10 },
  ],
  entityKinds: ['hollow'],
  entityBudget: 0,
  threatScale: 0.1,
  exitCount: 1,
  generator: 'hub',
};

const POOLROOMS: LevelSpec = {
  id: 'level-37',
  name: 'Level 37',
  subtitle: 'The Poolrooms',
  blurb: 'Warm water, white tile, and light from nowhere. Nothing has ever hurt anyone here.',
  gridW: 120,
  gridH: 120,
  cellSize: 3.4,
  ceilingHeight: 3.8,
  palette: {
    wall: 0xe9eef0,
    wallTrim: 0xc4d6dd,
    carpet: 0x9fd3dd,
    carpetStain: 0x7cbcc9,
    ceiling: 0xdfe9ec,
    lightTint: 0xd8f4ff,
    fog: 0x9dcfdb,
    ambient: 0x86b8c6,
  },
  fogNear: 11,
  fogFar: 64,
  ambientIntensity: 0.55,
  lightSpacing: 22,
  brokenLightChance: 0.05,
  supplyCount: 20,
  supplyTable: [
    { kind: 'almond-water', weight: 30 },
    { kind: 'water-bottle', weight: 20 },
    { kind: 'granola', weight: 14 },
    { kind: 'candy', weight: 12 },
    { kind: 'battery', weight: 10 },
    { kind: 'pills', weight: 8 },
    { kind: 'note', weight: 6 },
  ],
  /*
   * The one place the two numbering systems disagree and it matters. Wikidot's
   * Level 37 is "thought to be entirely devoid of life"; the Fandom system
   * calls the same place Level 7.1, and the Sticky-Centipede (Entity 60) is
   * documented on 7.1 along with 8, 7.5, 30 and 43 — every one of them a water
   * level. This game has no other standing water, so the centipede goes where
   * its counterplay exists or it never appears at all.
   *
   * Two honest divergences, recorded rather than hidden: the wikidot page for
   * this level says nothing lives here, and Entity 60 is described as passive
   * unless provoked. Ours is an ambush predator. The trade buys the level its
   * only mechanic — in water it out-runs a sprint, on dry tile it does not —
   * and `threatScale` stays at 0.35, the second-lowest in the game, so the
   * Poolrooms remain the quiet level they are supposed to be.
   */
  entityKinds: ['smiler', 'centipede'],
  entityBudget: 1,
  threatScale: 0.35,
  exitCount: 2,
  waterDepth: 0.28,
  generator: 'poolrooms',
};

const PIPES: LevelSpec = {
  id: 'level-2',
  name: 'Level 2',
  subtitle: 'Pipe Dreams',
  blurb: 'Service corridors that narrow the further you walk. Do not run here.',
  // Smaller than the other levels on purpose: a one-cell maze this dense makes
  // every metre of it cost, and the walk to an exit is long enough as it is.
  gridW: 106,
  gridH: 106,
  cellSize: 2.6,
  ceilingHeight: 2.5,
  palette: {
    wall: 0x6b6459,
    wallTrim: 0x4a453d,
    carpet: 0x3c3833,
    carpetStain: 0x2a2724,
    ceiling: 0x333029,
    lightTint: 0xffd9a0,
    fog: 0x2c2a26,
    ambient: 0x35322c,
  },
  fogNear: 5,
  fogFar: 38,
  ambientIntensity: 0.14,
  // Still the darkest level in the game by a distance — but at 50/0.45 only a
  // seventh of its floor was bright enough to recover sanity under, so it had
  // no recovery loop at all rather than a scarce one.
  lightSpacing: 30,
  brokenLightChance: 0.38,
  supplyCount: 18,
  supplyTable: [
    { kind: 'almond-water', weight: 18 },
    { kind: 'water-bottle', weight: 12 },
    { kind: 'granola', weight: 14 },
    { kind: 'canned-food', weight: 16 },
    { kind: 'battery', weight: 18 },
    { kind: 'bandage', weight: 10 },
    { kind: 'pills', weight: 8 },
    { kind: 'note', weight: 4 },
  ],
  /*
   * Wikidot's Level 2 entity list, filtered to what exists here: Hounds (pack
   * hunters that "drastically speed up into a chasing speed"), Facelings
   * ("incredibly intelligent entities" roaming in groups), Smilers, and Clumps
   * — which that page calls "one of the most lethal presences" on the level,
   * hunting from corners. Deathmoths are listed too but their population is
   * documented as collapsed here after the blackout, and this is the darkest
   * corridor level in the game, so they are left off.
   */
  entityKinds: ['hound', 'faceling', 'smiler', 'clump'],
  entityBudget: 2,
  threatScale: 1.35,
  exitCount: 3,
  generator: 'pipes',
};

/**
 * Level 4 — the Abandoned Office.
 *
 * "White, concrete-like walls and cheap carpet. The carpet is similar to Level
 * 0's, but it is grey and has no carpet fluid." The wiki's other detail is what
 * this level is built on: *most of the lights do not work, but the level is lit
 * by the constant light from the windows* — and most of those windows are
 * blacked out and glowing anyway.
 *
 * Every other floor in this game is lit by its fixtures, so the recipe here is
 * inverted: a high ambient and three quarters of the ceiling dead. That reads as
 * a completely different building before you have looked at anything, and it is
 * why this level does not feel like the Lobby despite being made of offices too.
 *
 * Survival difficulty 1, Almond Water in the coolers and the vending machines,
 * and — the wiki is explicit — "there are lots of wanderers on Level 4" because
 * so little lives here. It is the place a run gets its breath back before Level
 * 3, and it is the level the game most wants the player to stock up on.
 */
const OFFICES: LevelSpec = {
  id: 'level-4',
  name: 'Level 4',
  subtitle: 'Abandoned Office',
  blurb: 'The lights are off and the windows are painted over, and it is still bright enough to read by.',
  gridW: 130,
  gridH: 130,
  cellSize: 3.0,
  // Suspended-ceiling height. Lower than the Lobby by 20 cm, which is most of
  // why a floor plate this open still feels like an office.
  ceilingHeight: 2.9,
  palette: {
    wall: 0xe7e5df,
    wallTrim: 0xc6c3ba,
    carpet: 0x8d8c87,
    carpetStain: 0x6c6b66,
    ceiling: 0xdcdad3,
    // Daylight through painted glass: cold, and with none of the fluorescent
    // green every other interior in the game is lit by.
    lightTint: 0xdfe6ee,
    fog: 0xc8c7c1,
    ambient: 0xb7bac0,
  },
  fogNear: 12,
  fogFar: 62,
  // The windows are doing the work. Bright enough to recover sanity in, which
  // no other indoor level manages.
  ambientIntensity: 0.72,
  lightSpacing: 34,
  // "Most of the lights do not work." Three in four, which on a level this
  // bright is a detail rather than a threat — the dead fixture overhead is the
  // thing that tells you the light is coming from somewhere else.
  brokenLightChance: 0.74,
  supplyCount: 30,
  supplyTable: [
    { kind: 'almond-water', weight: 34 },
    { kind: 'water-bottle', weight: 20 },
    { kind: 'granola', weight: 14 },
    { kind: 'canned-food', weight: 10 },
    { kind: 'battery', weight: 10 },
    { kind: 'candy', weight: 6 },
    { kind: 'bandage', weight: 6 },
    { kind: 'pills', weight: 4 },
    { kind: 'note', weight: 6 },
  ],
  // Hounds and Dullers are the only entities the wiki has observed here, with a
  // single unverified Smiler report. There is no Duller in this game, so the
  // level runs on the two that are, at the lowest budget an occupied level has.
  entityKinds: ['hound', 'smiler'],
  entityBudget: 1,
  threatScale: 0.4,
  exitCount: 3,
  generator: 'offices',
};

/**
 * Level 3 — the Electrical Station.
 *
 * Brick in inconsistent colours, dusty concrete, dim fluorescents, and pipes of
 * black liquid along every wall. The wiki puts almost the whole documented
 * entity roster down here and has them hunting in organised brigades: Hounds,
 * Facelings, Deathmoths, Skin-Stealers, Smilers and Clumps, all at once. It also
 * says roughly seventy per cent of the M.E.G.'s supplies come off this level.
 *
 * Both halves of that are true of this spec. It is the best-stocked floor in
 * the game and the only one where six kinds of thing can be in the building at
 * the same time — but never more than two at once, because a level that is
 * frightening because of how many things are on it stops being frightening
 * about ten minutes in.
 */
const STATION: LevelSpec = {
  id: 'level-3',
  name: 'Level 3',
  subtitle: 'Electrical Station',
  blurb: 'Something in the walls is still running. The pipes are full and none of it is water.',
  gridW: 132,
  gridH: 132,
  // Tight cells: the level's premise is that a corridor can be one cell wide
  // and the next room forty metres across, and the crawls have to actually
  // squeeze at this scale.
  cellSize: 2.8,
  ceilingHeight: 3.8,
  palette: {
    wall: 0x77483a,
    wallTrim: 0x54332a,
    carpet: 0x69645b,
    carpetStain: 0x48443e,
    ceiling: 0x3b3833,
    // Old fluorescent tubes that have gone green with age.
    lightTint: 0xe4efd4,
    fog: 0x3d2f28,
    ambient: 0x4a3f37,
  },
  fogNear: 6,
  fogFar: 44,
  ambientIntensity: 0.2,
  // The fixtures work; they are simply too far apart and too dim, which is what
  // "dim fluorescent ceiling lights" in a plant this size actually looks like.
  lightSpacing: 20,
  brokenLightChance: 0.24,
  // The assembly lines. Nothing else in the descent is stocked like this, and
  // the level needs it: this is where a player gets the reserves that carry
  // them through the hotel and the dark.
  supplyCount: 34,
  supplyTable: [
    { kind: 'almond-water', weight: 24 },
    { kind: 'battery', weight: 20 },
    { kind: 'canned-food', weight: 16 },
    { kind: 'water-bottle', weight: 12 },
    { kind: 'granola', weight: 10 },
    { kind: 'bandage', weight: 8 },
    { kind: 'pills', weight: 6 },
    { kind: 'note', weight: 4 },
  ],
  // Wikidot's Level 3 roster almost intact — Hounds, Facelings, Deathmoths,
  // Smilers and Clumps travel this level in brigades. Skin-Stealers are on that
  // list and are genuinely canonical (Entity 10); what this game had under the
  // name was not, and it has been retired rather than misrepresented.
  entityKinds: ['hound', 'faceling', 'deathmoth', 'smiler', 'clump'],
  entityBudget: 2,
  threatScale: 1.45,
  exitCount: 3,
  generator: 'station',
};

/**
 * Level 5 — the Terror Hotel.
 *
 * Mahogany red and gold, dark walnut, carpet with a pattern in it, and a
 * turntable playing somewhere you never find. Built in the 1930s and furnished
 * in the 1920s, and — the line that decides how this level is lit and how clean
 * it is — "the level is mostly clean, with little dust and dirt lingering on
 * surfaces". Something is still doing the housekeeping.
 *
 * So this is the best-maintained floor in the game: nine fixtures in ten are
 * working, at a corridor pitch rather than a warehouse one. That matters
 * mechanically as well as visually, because the level's own entity is the
 * Deathmoth, which the lore has drawn to light and to whoever is carrying it —
 * and `entities/defs.ts` builds that literally: a moth sees a player holding a
 * lit torch from nearly forty metres and one in an unlit room from eight.
 *
 * The lighting and the roster are therefore the same decision. This is the only
 * level in the game bright enough to walk with the torch off, and it is the
 * only level where doing so is the difference between being hunted and not.
 * Turning the flashlight off in a corridor you can still see down is the one
 * piece of play this level is for; if either number moves, the other has to.
 */
const HOTEL: LevelSpec = {
  id: 'level-5',
  name: 'Level 5',
  subtitle: 'Terror Hotel',
  blurb: 'Nine hundred doors, all of them numbered, and somebody has been hoovering.',
  // Smaller than it wants to be. The corridors run the full width whatever this
  // is, so the level still has the longest sight lines in the game — but a
  // corridor grid makes every walk a Manhattan one, and at 136 the nearest way
  // down was consistently over four hundred metres away. See `exitCount`.
  gridW: 126,
  gridH: 126,
  cellSize: 3.0,
  ceilingHeight: 3.6,
  palette: {
    wall: 0x7c2b27,
    wallTrim: 0xb7913f,
    carpet: 0x77262a,
    carpetStain: 0x561b1e,
    ceiling: 0xe6d9bb,
    // Filament, not fluorescent. The only warm light in the descent.
    lightTint: 0xffd49a,
    fog: 0x33161a,
    ambient: 0x543228,
  },
  fogNear: 8,
  fogFar: 48,
  ambientIntensity: 0.28,
  // Half the Lobby's spacing and a tenth of its failures. A hotel corridor is
  // lit every few metres and this one is still being looked after.
  lightSpacing: 12,
  brokenLightChance: 0.1,
  supplyCount: 24,
  supplyTable: [
    { kind: 'almond-water', weight: 24 },
    { kind: 'water-bottle', weight: 18 },
    { kind: 'candy', weight: 14 },
    { kind: 'granola', weight: 14 },
    { kind: 'canned-food', weight: 10 },
    { kind: 'battery', weight: 8 },
    { kind: 'bandage', weight: 6 },
    { kind: 'pills', weight: 4 },
    { kind: 'note', weight: 6 },
  ],
  // Deathmoths are the level's own — the wiki has large nesting populations
  // through the main hallways — with Hounds behind them. (Skin-Stealers are on
  // the wiki's list; this game's entity of that name is retired.) The pairing
  // is better than it looks: this is the best-lit floor in the game, and the
  // Deathmoth is the kind that crosses a level for a light.
  entityKinds: ['deathmoth', 'hound'],
  entityBudget: 2,
  threatScale: 0.9,
  // Four rather than three. A corridor grid makes every walk a Manhattan one, so
  // the far-quartile rule that places the ordinary exits puts them a long way
  // off however many there are; the fourth stairwell is what brings the nearest
  // way down back into the range every other level sits in.
  exitCount: 4,
  generator: 'hotel',
};

/**
 * Level 6 — Lights Out.
 *
 * "A seemingly endless series of tight hallways made of a smooth, cold material
 * (likely concrete)", in the dark, in silence. The wiki records no entities on
 * this level at all — only paranoia, dread, and explorers hearing scuttling and
 * breathing and whispering that is not there. The note it ends on is the whole
 * design brief: *Level 6 isn't dangerous because there's something there. It's
 * dangerous because there's nothing there.*
 *
 * That is taken literally. `entityKinds` is the hallucination and nothing else,
 * and `entityBudget` is zero, so the director can never buy a real spawn here —
 * every point of pressure it accrues is spent on a fakeout instead. The threat
 * scale is high, which on any other level would mean things arriving and here
 * means the noises never stop. It is the only floor in the game that is loud
 * and empty at the same time, and it stays that way on every loop, because a
 * hallucination has a spawn weight of zero however deep the descent goes.
 *
 * The one place this parts company with the wiki is the torch. Canon says light
 * sources brought into Level 6 do not work; a game that takes the flashlight
 * away on the level that needs it most is not frightening, it is unfair. So the
 * torch works and the level is simply darker than anything else by a distance —
 * 16 m of visibility, an ambient of almost nothing, and the only fixtures
 * burning anywhere on it are the ones the generator hangs over the way out.
 */
const LIGHTS_OUT: LevelSpec = {
  id: 'level-6',
  name: 'Level 6',
  subtitle: 'Lights Out',
  blurb: 'Concrete, both hands on the walls, and no way to tell how long you have been walking.',
  // Small, because everything about crossing it is slow. At 2.4 m cells this is
  // still 230 metres across and it takes as long as any level in the game.
  gridW: 96,
  gridH: 96,
  cellSize: 2.4,
  ceilingHeight: 2.6,
  palette: {
    wall: 0x2b2b2d,
    wallTrim: 0x1f1f21,
    carpet: 0x242426,
    carpetStain: 0x1b1b1d,
    ceiling: 0x1d1d1f,
    lightTint: 0x9fb0c0,
    fog: 0x050506,
    ambient: 0x0b0b0d,
  },
  // You can see six metres. Past that there is not darkness, there is nothing.
  fogNear: 2,
  fogFar: 16,
  ambientIntensity: 0.03,
  // Effectively none: at this spacing the level places a handful across four
  // hundred metres, and `lightExits` adds one over each way out afterwards. On
  // Level 6 a light in the distance is not scenery, it is the exit.
  lightSpacing: 420,
  brokenLightChance: 0.96,
  // The wiki gives this level no supplies at all, which as a rule would make it
  // a death trap rather than a dark one. What is here is what other people
  // carried in and put down, so it is mostly batteries.
  supplyCount: 12,
  supplyTable: [
    // Torches, not cells: on this level the beam never runs down, so a battery
    // would be a pickup that does nothing. Spares instead, because losing your
    // only light here is the difference between a level and a soft lock.
    { kind: 'flashlight', weight: 34 },
    { kind: 'battery', weight: 6 },
    { kind: 'almond-water', weight: 16 },
    { kind: 'granola', weight: 12 },
    { kind: 'water-bottle', weight: 10 },
    { kind: 'canned-food', weight: 8 },
    { kind: 'bandage', weight: 8 },
    { kind: 'pills', weight: 6 },
    { kind: 'note', weight: 8 },
  ],
  // Every level before this one can hand out a torch; none of them is obliged
  // to. Arriving here without one would be a run ended by a dice roll four
  // floors ago, so this level guarantees one the way Level 0 does.
  guaranteedItems: ['flashlight'],
  freeFlashlight: true,
  entityKinds: ['hollow'],
  entityBudget: 0,
  threatScale: 1.25,
  exitCount: 3,
  generator: 'lightsout',
};

/**
 * Level 11 — the Endless City.
 *
 * Concrete sidewalks, asphalt with yellow lines on it, an overcast sky that
 * never arrives anywhere, and a grid of roads that goes on past anything you
 * can see. About a third of the buildings are sealed — no doors, black glass,
 * nothing to find — and the rest are open, furnished and pointless.
 *
 * The entity note is the interesting one. Facelings and Hounds are the only
 * things that spawn here regularly, and the wiki has them subject to what it
 * calls the 11 Effect: on this level they behave like pedestrians and dogs, and
 * they are not interested in you. So the roster is exactly those two and the
 * threat scale is a third of what the same pair costs anywhere else. Something
 * with no face walking past you on the other side of the road, at the pace of
 * somebody going to work, is worse than the same thing running at you.
 *
 * The wikis disagree about the name. Wikidot's current page calls Level 11 "The
 * City That Never Sleeps"; the archived original and every game and Fandom wiki
 * that followed it call it "The Endless City". That is the version the player
 * has heard of, so that is the one on the card.
 */
const ENDLESS_CITY: LevelSpec = {
  id: 'level-11',
  name: 'Level 11',
  subtitle: 'The Endless City',
  blurb: 'Every road meets another road. None of them leaves.',
  gridW: 132,
  gridH: 132,
  cellSize: 3.4,
  // Building height rather than room height: the streets have no ceiling, and
  // the renderer carries the facades up past this wherever an open lane meets
  // one, so this number is what decides how tall the city is.
  ceilingHeight: 4.6,
  palette: {
    wall: 0x8d8d89,
    wallTrim: 0x6d6d69,
    carpet: 0x45454b,
    carpetStain: 0x5b5b61,
    ceiling: 0x999995,
    // Sodium street lamps, indoors and out. "Some lamps are on, some are off."
    lightTint: 0xffe6b8,
    fog: 0xa7acb1,
    ambient: 0x99a2ab,
  },
  // Overcast, so the far end of the street is a grey wall rather than a horizon.
  // Long enough to see three junctions down and never far enough to see the end
  // of anything, which is the level's entire argument.
  fogNear: 22,
  fogFar: 86,
  viewDistance: 92,
  ambientIntensity: 0.78,
  lightSpacing: 26,
  brokenLightChance: 0.45,
  supplyCount: 28,
  supplyTable: [
    { kind: 'water-bottle', weight: 22 },
    { kind: 'almond-water', weight: 18 },
    { kind: 'canned-food', weight: 16 },
    { kind: 'granola', weight: 14 },
    { kind: 'candy', weight: 10 },
    { kind: 'battery', weight: 10 },
    { kind: 'bandage', weight: 6 },
    { kind: 'note', weight: 6 },
  ],
  entityKinds: ['faceling', 'hound'],
  entityBudget: 2,
  threatScale: 0.5,
  exitCount: 3,
  // The canal. Shallow, and roofed for its whole length because a cell with
  // water in it has a ceiling over it — which is what makes it a culvert rather
  // than a river, and the only dark place on the level.
  waterDepth: 0.3,
  generator: 'city',
};

/**
 * The Funrooms. The party dimension, and the only level in the game that is
 * trying to be nice to you.
 *
 * Everything here is at the settings a children's entertainer would have picked
 * — primary colours, a scrubbed hard floor, twice as many lights as the room
 * needs — and none of it has been touched. There is nothing hidden in the
 * decorations and nothing written on the walls. It is uncomfortable because it
 * is cheerful, and it stays cheerful whatever happens in it.
 */
const FUNROOMS: LevelSpec = {
  id: 'level-fun',
  name: 'Level Fun',
  subtitle: 'The Funrooms',
  blurb: 'Somebody set out for a party a very long time ago. Everything is still ready.',
  gridW: 118,
  gridH: 118,
  cellSize: 3.4,
  ceilingHeight: 4.2,
  palette: {
    // Saturated to the point of being hard to look at, and lit hard enough that
    // nothing in here has a shadow to be hiding in.
    wall: 0xf2c516,
    wallTrim: 0xe0453c,
    carpet: 0x2f6fd0,
    carpetStain: 0x2455a0,
    ceiling: 0xfaf3e2,
    lightTint: 0xfff2f6,
    fog: 0xe7b6c6,
    ambient: 0xd6a25f,
  },
  fogNear: 11,
  fogFar: 60,
  ambientIntensity: 0.62,
  // Roughly twice the Lobby's fixture density, and almost none of them out. The
  // one thing you cannot do on this level is find somewhere dim to stand.
  lightSpacing: 7,
  brokenLightChance: 0.02,
  supplyCount: 24,
  supplyTable: [
    { kind: 'candy', weight: 32 },
    { kind: 'almond-water', weight: 18 },
    { kind: 'granola', weight: 14 },
    { kind: 'water-bottle', weight: 12 },
    { kind: 'canned-food', weight: 8 },
    { kind: 'battery', weight: 8 },
    { kind: 'bandage', weight: 4 },
    { kind: 'note', weight: 4 },
  ],
  // The Funrooms is where a Level 0 slide comes out, and the slides are the
  // most obvious thing on Level 0 — so a player can arrive here having followed
  // the signposting perfectly and skipped the one torch the game guarantees. A
  // second one is not waste: `survival.ts` turns a duplicate into a full set of
  // cells. Being three floors deep with no light because you took the way down
  // the level was pointing at would be the game punishing its own advice.
  guaranteedItems: ['flashlight'],
  /*
   * The Party, and nothing else. Entity 67's documented habitats are Level Fun,
   * 119 and 188, and the level is theirs — the wiki's population estimate runs
   * to the tens of thousands. Nothing else is documented here, so nothing else
   * is listed.
   *
   * Budget 2 is the point rather than an escalation: "you can never catch a
   * Partygoer alone" is canon, and the behaviour is built around it — one
   * approaches cheerfully, drops the act, and calls the floor. A lone Partygoer
   * is the entity with its mechanic removed. `threatScale` is unchanged, so a
   * spawn is no more likely than it was; when one comes, it brings company.
   */
  entityKinds: ['partygoer'],
  entityBudget: 2,
  threatScale: 0.85,
  exitCount: 2,
  generator: 'funrooms',
};

/**
 * Level 94 — Motion. A neighbourhood, at the scale of a neighbourhood, built by
 * somebody who had only ever had one described to them.
 *
 * The only level with no roof over most of it: the lanes are `Cell.Open`, so
 * the sky is whatever the fog is, and the tallest thing for four hundred metres
 * is the white water tower in the middle. That tower is the point of the layout
 * — it is visible from anywhere the fog reaches, so this is the one level where
 * being lost is a choice.
 */
const MOTION: LevelSpec = {
  id: 'level-94',
  name: 'Level 94',
  subtitle: 'Motion',
  blurb: 'Houses, lawns, a water tower. The grass is the same green everywhere, and it is the wrong green.',
  gridW: 112,
  gridH: 112,
  cellSize: 3.2,
  // House height rather than room height. The renderer's collar takes the walls
  // up past this wherever an open lane meets one, which is what makes a row of
  // houses read as buildings instead of as hedges.
  ceilingHeight: 3.4,
  palette: {
    wall: 0xf4f1e6,
    wallTrim: 0xd9483f,
    carpet: 0x6cb43e,
    carpetStain: 0x4f9433,
    ceiling: 0xf7f4ea,
    lightTint: 0xfff8e4,
    fog: 0xa9d9ea,
    ambient: 0xc2dcff,
  },
  // The one long view in the game. Everywhere else the fog is a wall you cannot
  // see past and there is nothing past it worth drawing; here the level is a
  // neighbourhood arranged around a landmark, and a landmark you cannot see is
  // just a tall thing you walk into. 88 m of daylight puts the tower in sight
  // from a quarter of the map and still hides the far edge, so the level is
  // navigable without being surveyable. It costs draw calls — see
  // `LevelSpec.viewDistance` and `Renderer.cullProps`, which keeps ordinary
  // scenery on the short radius so only the tower is paid for at range.
  fogNear: 30,
  fogFar: 112,
  viewDistance: 118,
  // Daylight. It is bright enough out here to get sanity back just by standing
  // in it, which is the joke: the safest-feeling place in the game is the one
  // that is most obviously made up.
  ambientIntensity: 0.95,
  lightSpacing: 30,
  brokenLightChance: 0.06,
  supplyCount: 18,
  supplyTable: [
    { kind: 'water-bottle', weight: 22 },
    { kind: 'almond-water', weight: 18 },
    { kind: 'granola', weight: 16 },
    { kind: 'candy', weight: 14 },
    { kind: 'canned-food', weight: 10 },
    { kind: 'battery', weight: 8 },
    { kind: 'bandage', weight: 6 },
    { kind: 'note', weight: 6 },
  ],
  // For the reason the Funrooms has one: a slide off Level 0 can land here, so
  // this is now a floor a player can reach without ever having walked past the
  // guaranteed torch. Daylight makes it less urgent than it would be anywhere
  // else, but the level after this one is the Lobby on its next loop.
  guaranteedItems: ['flashlight'],
  /*
   * Wikidot's Level 94 list is Animations, Smilers, Skin-Stealers, Hounds, Male
   * Deathmoths, Death Rats, Robo-Men and The Animated King. Filtered to what
   * exists here that is Smilers, Hounds and Deathmoths.
   *
   * `hollow` was on this roster and was a real bug, not just noise:
   * `placeEntities` picks a level's opening spawns *uniformly* from
   * `entityKinds` and does not consult the director's spawn weights, so half of
   * all Level 94 runs began with a hallucination standing in the neighbourhood
   * from the moment of load. A hollow is bought with sanity or not at all.
   */
  entityKinds: ['smiler', 'hound', 'deathmoth'],
  entityBudget: 1,
  threatScale: 0.3,
  exitCount: 2,
  generator: 'motion',
};

/**
 * The descent order. Wraps around to deeper, harder repeats past the end.
 *
 * Twelve floors before anything comes round again, which is the point of it:
 * past the end `getLevelSpec` can only offer the same level with the threat
 * turned up, and a run that reaches that has stopped being about finding places
 * you have not seen. Every level here is a documented Backrooms level and the
 * order is not the numbering — the wikis' Level 2 is the fifth floor down and
 * their Level 37 is the fourth — because what this list is actually sequencing
 * is pressure. Dangerous floors alternate with somewhere to breathe:
 *
 *     0    1    1.2  37   2    4    3    5    6    11   Fun  94
 *     .7   1.0  .1   .35  1.35 .4   1.45 .9   1.25 .5   .85  .3
 *
 * The three worst — Level 3, Level 2 and Level 6 — are never adjacent. Level 3
 * is both the hardest floor in the game and the best supplied, and the level
 * immediately before it is the second best supplied: thirty caches of Level 4's
 * Almond Water and then thirty-four of Level 3's own is the only reason the
 * spike in the middle of this list is survivable.
 *
 * The Funrooms and Motion sit at the end of it, but almost nobody arrives there
 * that way: they are what the slides are for, and a slide is the only thing in
 * the game that skips (see `SLIDE_ROUTES`).
 */
export const LEVEL_ORDER: readonly LevelSpec[] = [
  LOBBY,
  WAREHOUSE,
  ALCOVE,
  POOLROOMS,
  PIPES,
  OFFICES,
  STATION,
  HOTEL,
  LIGHTS_OUT,
  ENDLESS_CITY,
  FUNROOMS,
  MOTION,
];

/**
 * Level spec for a descent index. Past the authored list the levels repeat with
 * scaled-up threat and thinner supplies, so a run can always go deeper.
 */
export function getLevelSpec(index: number): LevelSpec {
  const base = LEVEL_ORDER[index % LEVEL_ORDER.length]!;
  const loop = Math.floor(index / LEVEL_ORDER.length);
  if (loop === 0) return base;
  return {
    ...base,
    name: `${base.name}`,
    subtitle: `${base.subtitle} — deeper`,
    supplyCount: Math.max(8, Math.round(base.supplyCount * Math.pow(0.82, loop))),
    entityBudget: base.entityBudget + loop,
    threatScale: base.threatScale * (1 + 0.45 * loop),
    brokenLightChance: Math.min(0.8, base.brokenLightChance + 0.12 * loop),
  };
}

export const LEVEL_COUNT = LEVEL_ORDER.length;

/* ------------------------------------------------------------------ *
 * Slides
 * ------------------------------------------------------------------ */

/**
 * Where the slides on a level come out, keyed by the id of the level they are
 * on, canonical destination first.
 *
 * This is the only routing in the game that is not "the next one down". Every
 * other way out drops you one floor; a slide is a hole in the order, and it
 * goes somewhere with a name. A level absent from this table has no slide, so
 * finding one still means something — the Alcove has none because the Alcove is
 * the one place the building behaves.
 *
 * A level carries several mouths now (see `world/gen/slides.ts`), and a row
 * with two destinations in it is what keeps them from being several ways to the
 * same place: the first mouth placed takes the head of the list and the rest
 * cycle through the tail, so riding one is still a decision with an unknown on
 * the other side of it. Nothing tells the player which mouth goes where, and
 * nothing should — the building does not label its own plumbing.
 */
const SLIDE_ROUTES: Readonly<Record<string, readonly string[]>> = {
  // Every row needs at least two outlets that are not each other's only outlet.
  // The first version of this table had `level-fun` going only to Level 94 and
  // Level 94 going only back to the Lobby, which made Lobby -> Funrooms ->
  // Motion -> Lobby a closed three-cycle: a player who took one slide never saw
  // Offices, Station, Hotel, Lights Out or the City again, because nothing
  // routed *into* them from anywhere reachable. Outlets are chosen from what
  // the wikis document connecting, but the hard rule is structural — no level
  // may be a dead end that feeds one other level.
  'level-0': ['level-4', 'level-fun', 'level-3'],
  'level-1': ['level-3', 'level-94', 'level-5'],
  'level-37': ['level-fun', 'level-11'],
  'level-2': ['level-6', 'level-94', 'level-11'],
  // Level 4's stairways and lifts are recorded as coming out on 5 and 6.
  'level-4': ['level-5', 'level-6', 'level-11'],
  // Level 3's ladders come up through a manhole into the streets of Level 11.
  'level-3': ['level-11', 'level-fun', 'level-5'],
  // The Boiler Room: the one part of the hotel that is not kept, and where the
  // wiki puts the way down to Level 6.
  'level-5': ['level-6', 'level-11', 'level-94'],
  // Lights Out gives the player nothing. It is the one level with no slide out,
  // and that is the point of it — you leave the ordinary way or not at all.
  'level-11': ['level-fun', 'level-94', 'level-4'],
  'level-fun': ['level-94', 'level-5', 'level-11'],
  'level-94': ['level-0', 'level-11', 'level-6'],
};

/**
 * The level-order indices the slides on `spec` lead to, canonical first, or an
 * empty array when this level has no slide. Generators call this:
 * `ExitPoint.target` is an index into `LEVEL_ORDER`, and the generator never
 * knows which descent index it is being built for.
 */
/**
 * Where a way down actually leads, given where this run has already been.
 *
 * Two things have to be true at once, and the first version of this only
 * managed the second. **Coverage**: a run should see the whole building before
 * it repeats any of it. **Variety**: two runs should not be the same tour.
 * Returning the first unvisited route satisfied coverage and produced the
 * identical twelve-level sequence every single time.
 *
 * So the choice is a draw from everywhere unvisited, not a scan. A slide draws
 * from its own route table first — that is what makes slides the interesting
 * way down, and what keeps the documented connections between famous levels
 * meaningful — and falls through to anywhere unseen once its routes are
 * exhausted. An ordinary way down draws from everywhere unseen directly.
 *
 * `-1` means everywhere has been seen; the caller then takes the next floor in
 * order, which is where the deeper, harder repeats begin.
 *
 * The RNG is required rather than optional on purpose. It has to be a stream
 * owned by the run so that a seed still reproduces a run exactly — the game
 * promises that — and making it a parameter means nobody can quietly call this
 * without one.
 */
export function pickSlideTarget(
  spec: LevelSpec,
  visited: ReadonlySet<string>,
  rng: RNG,
  preferRoutes = true,
): number {
  if (preferRoutes) {
    const routed = slideTargets(spec).filter((i) => !visited.has(LEVEL_ORDER[i]!.id));
    if (routed.length > 0) return rng.pick(routed);
  }
  const fresh: number[] = [];
  for (let i = 0; i < LEVEL_ORDER.length; i++) {
    if (!visited.has(LEVEL_ORDER[i]!.id)) fresh.push(i);
  }
  return fresh.length > 0 ? rng.pick(fresh) : -1;
}


export function slideTargets(spec: LevelSpec): number[] {
  const destinations = SLIDE_ROUTES[spec.id];
  if (destinations === undefined) return [];
  const out: number[] = [];
  for (const id of destinations) {
    const index = LEVEL_ORDER.findIndex((s) => s.id === id);
    if (index >= 0) out.push(index);
  }
  return out;
}

/**
 * The descent index an exit actually leads to.
 *
 * `ExitPoint.target` names a *level*, not a depth, and the descent is unbounded
 * — past `LEVEL_COUNT` the order repeats with the threat scaled up. So a target
 * is resolved inside the loop the player is already on, and if that lands on or
 * behind where they are it is pushed one loop deeper. Two things follow, both
 * of them wanted: depth never goes down, so a slide can never be ridden in a
 * circle to farm a shallow level; and the slide out of Motion, which points at
 * Level 0, hands the player back the Lobby rather than the run.
 *
 * A target that is out of range, or missing, means "the next one".
 */
export function resolveDescent(from: number, target: number): number {
  if (!Number.isInteger(target) || target < 0 || target >= LEVEL_COUNT) return from + 1;
  const loop = Math.floor(from / LEVEL_COUNT);
  const to = loop * LEVEL_COUNT + target;
  return to <= from ? to + LEVEL_COUNT : to;
}
